import { OrderIssueType, StageProgress } from '../../../models/APIModels';

/**
 * The warehouse views as storage-api sends them, and the rows the tab shows.
 *
 * The tab was written against shapes the API never had — `orderId`, `date`,
 * `boxes`, a `rows` list of storage charges — so every cell read undefined and
 * rendered "—", and Storage read a list that does not exist. Inbound and outbound
 * come back one row per box under `orders`; the tab shows one row per order.
 */

/** One box on `/warehouse/inbound` or `/warehouse/outbound`. */
export interface WarehouseBoxRow {
  oid: number;
  bid?: string | null;
  orderBoxId?: number;
  customer?: string;
  courier?: string | null;
  eta?: string | null;
  status?: string;
  statusCode?: string;
  /** Inbound only, dd/MM/yyyy. */
  collectionDate?: string;
  /** Outbound only, dd/MM/yyyy. */
  deliveryDate?: string;
  unloadProgress?: StageProgress | null;
  pickProgress?: StageProgress | null;
  holdoutProgress?: StageProgress | null;
  onsProgress?: StageProgress | null;
  issues?: { type: OrderIssueType }[];
  /** Outbound only: the return courier HubSpot records, and the DPD label state. */
  returnCourier?: string | null;
  needsDpdLabel?: boolean;
  hasDpdLabel?: boolean;
}

export interface WarehouseBoxesResponse {
  orders?: WarehouseBoxRow[];
  totalOrders?: number;
  totalBoxes?: number;
}

/** One order on the Inbound or Outbound view. */
export interface WarehouseOrderRow {
  orderId: number;
  customer: string;
  courier: string | null;
  date: string | null;
  eta: string | null;
  boxes: number;
  status: string;
  unloadProgress?: StageProgress | null;
  pickProgress?: StageProgress | null;
  holdoutProgress?: StageProgress | null;
  onsProgress?: StageProgress | null;
  issues: { type: OrderIssueType }[];
  returnCourier: string | null;
  needsDpdLabel: boolean;
  hasDpdLabel: boolean;
}

/** "Due Out" for one status; "2 Not scanned · 1 Due Out" for a mix, commonest first. */
export function statusSummary(statuses: string[]): string {
  const counts = new Map<string, number>();
  statuses.forEach((s) => counts.set(s, (counts.get(s) ?? 0) + 1));
  if (counts.size === 0) return '—';
  if (counts.size === 1) return statuses[0];
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([s, n]) => `${n} ${s}`)
    .join(' · ');
}

/**
 * One row per order, in the order the API first lists each one. The progress and
 * issue figures are per order already, repeated on every box, so the first box's
 * are the order's.
 */
export function groupBoxesByOrder(
  rows: WarehouseBoxRow[],
  dateKey: 'collectionDate' | 'deliveryDate',
): WarehouseOrderRow[] {
  const byOrder = new Map<number, WarehouseBoxRow[]>();
  rows.forEach((r) => {
    const list = byOrder.get(r.oid);
    if (list) list.push(r);
    else byOrder.set(r.oid, [r]);
  });
  return Array.from(byOrder.entries()).map(([orderId, boxes]) => {
    const first = boxes[0];
    return {
      orderId,
      customer: first.customer || '—',
      courier: first.courier ?? null,
      date: first[dateKey] ?? null,
      eta: boxes.find((b) => b.eta)?.eta ?? null,
      boxes: boxes.length,
      status: statusSummary(boxes.map((b) => b.status || '—')),
      unloadProgress: first.unloadProgress,
      pickProgress: first.pickProgress,
      holdoutProgress: first.holdoutProgress,
      onsProgress: first.onsProgress,
      issues: first.issues ?? [],
      returnCourier: first.returnCourier ?? null,
      needsDpdLabel: boxes.some((b) => b.needsDpdLabel),
      hasDpdLabel: boxes.some((b) => b.hasDpdLabel),
    };
  });
}

/** `/warehouse/storage`. */
export interface WarehouseStorageResponse {
  storageHistory?: { date: string; count: number }[];
  currentBoxesInStorage?: number;
  /** How many of the current figure were counted by booking dates, never having been scanned. */
  unscannedBoxesInStorage?: number;
}

export interface StorageSummary {
  current: number;
  unscanned: number;
  history: { date: string; count: number }[];
}

export function storageSummary(data: WarehouseStorageResponse | null | undefined): StorageSummary {
  return {
    current: data?.currentBoxesInStorage ?? 0,
    unscanned: data?.unscannedBoxesInStorage ?? 0,
    history: data?.storageHistory ?? [],
  };
}

/** One box on `/warehouse/box-history`. */
export interface BoxHistoryRow {
  name?: string;
  oid?: number;
  bid?: string | null;
  collectionDate?: string | null;
  collectionCourier?: string | null;
  lastScanDate?: string | null;
  lastBoxStatus?: string;
  warehouse?: string | null;
  deliveryDate?: string | null;
  deliveryCourier?: string | null;
}

export interface BoxHistoryResponse {
  boxes?: BoxHistoryRow[];
  totalCount?: number;
}

/** Where "Create DPD label" goes: the order page, which opens the DPD modal on `?dpd=1`. */
export function dpdLabelPath(orderId: number): string {
  return `/admin/order/${orderId}?dpd=1`;
}
